import type { BodyZone } from '../types/booking';

export interface PiercingZoneGuide {
  src: string;
  /** i18n key under piercingBooking.zoneGuide.* */
  titleKey: string;
  captionKey: string;
  altKey: string;
}

/** Guides keyed by official body zone ids from the catalog import. */
const GUIDES_BY_ZONE_ID: Record<string, PiercingZoneGuide> = {
  'body-zone-oreille': {
    src: '/images/piercing-guides/oreille.jpg',
    titleKey: 'earTitle',
    captionKey: 'earCaption',
    altKey: 'earAlt',
  },
  'body-zone-nez': {
    src: '/images/piercing-guides/nez.jpg',
    titleKey: 'noseTitle',
    captionKey: 'noseCaption',
    altKey: 'noseAlt',
  },
  'body-zone-nombril': {
    src: '/images/piercing-guides/nombril.jpg',
    titleKey: 'navelTitle',
    captionKey: 'navelCaption',
    altKey: 'navelAlt',
  },
  'body-zone-sourcil': {
    src: '/images/piercing-guides/sourcil.jpg',
    titleKey: 'eyebrowTitle',
    captionKey: 'eyebrowCaption',
    altKey: 'eyebrowAlt',
  },
  'body-zone-arcade': {
    src: '/images/piercing-guides/sourcil.jpg',
    titleKey: 'eyebrowTitle',
    captionKey: 'eyebrowCaption',
    altKey: 'eyebrowAlt',
  },
  'body-zone-bridge': {
    src: '/images/piercing-guides/sourcil.jpg',
    titleKey: 'eyebrowTitle',
    captionKey: 'eyebrowCaption',
    altKey: 'eyebrowAlt',
  },
  'body-zone-langue': {
    src: '/images/piercing-guides/langue.jpg',
    titleKey: 'tongueTitle',
    captionKey: 'tongueCaption',
    altKey: 'tongueAlt',
  },
  'body-zone-bouche': {
    src: '/images/piercing-guides/langue.jpg',
    titleKey: 'tongueTitle',
    captionKey: 'tongueCaption',
    altKey: 'tongueAlt',
  },
  'body-zone-tour-de-bouche': {
    src: '/images/piercing-guides/levre.jpg',
    titleKey: 'lipTitle',
    captionKey: 'lipCaption',
    altKey: 'lipAlt',
  },
  'body-zone-joue': {
    src: '/images/piercing-guides/levre.jpg',
    titleKey: 'lipTitle',
    captionKey: 'lipCaption',
    altKey: 'lipAlt',
  },
  // Legacy ids (pre-official catalog) kept for resilience
  'body-zone-ear': {
    src: '/images/piercing-guides/oreille.jpg',
    titleKey: 'earTitle',
    captionKey: 'earCaption',
    altKey: 'earAlt',
  },
  'body-zone-nose': {
    src: '/images/piercing-guides/nez.jpg',
    titleKey: 'noseTitle',
    captionKey: 'noseCaption',
    altKey: 'noseAlt',
  },
  'body-zone-navel': {
    src: '/images/piercing-guides/nombril.jpg',
    titleKey: 'navelTitle',
    captionKey: 'navelCaption',
    altKey: 'navelAlt',
  },
  'body-zone-eyebrow': {
    src: '/images/piercing-guides/sourcil.jpg',
    titleKey: 'eyebrowTitle',
    captionKey: 'eyebrowCaption',
    altKey: 'eyebrowAlt',
  },
  'body-zone-tongue': {
    src: '/images/piercing-guides/langue.jpg',
    titleKey: 'tongueTitle',
    captionKey: 'tongueCaption',
    altKey: 'tongueAlt',
  },
  'body-zone-lip': {
    src: '/images/piercing-guides/levre.jpg',
    titleKey: 'lipTitle',
    captionKey: 'lipCaption',
    altKey: 'lipAlt',
  },
};

function normalizeZoneToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Resolve a visual piercing guide for the selected body zone.
 * Genital / surface / dermal / teton: no guide image (dropdown-only).
 */
export function getPiercingZoneGuide(zone?: BodyZone | null): PiercingZoneGuide | null {
  if (!zone) return null;

  if (GUIDES_BY_ZONE_ID[zone.id]) {
    return GUIDES_BY_ZONE_ID[zone.id];
  }

  const name = normalizeZoneToken(zone.name || '');
  const id = normalizeZoneToken(zone.id || '');
  const category = normalizeZoneToken(zone.category || '');

  // No imagery for intimate / surface zones (meeting constraint)
  if (
    name.includes('genital') ||
    category === 'genital' ||
    name.includes('surface') ||
    name.includes('dermal') ||
    name.includes('teton') ||
    name.includes('nipple')
  ) {
    return null;
  }

  if (name.includes('oreille') || name.includes('ear') || id.includes('oreille') || id.includes('ear')) {
    return GUIDES_BY_ZONE_ID['body-zone-oreille'];
  }

  if (name.includes('nez') || name.includes('nose') || id.includes('nez') || id.includes('nose')) {
    return GUIDES_BY_ZONE_ID['body-zone-nez'];
  }

  if (
    name.includes('nombril') ||
    name.includes('navel') ||
    name.includes('belly') ||
    id.includes('nombril') ||
    id.includes('navel')
  ) {
    return GUIDES_BY_ZONE_ID['body-zone-nombril'];
  }

  if (
    name.includes('sourcil') ||
    name.includes('arcade') ||
    name.includes('eyebrow') ||
    name === 'bridge' ||
    id.includes('sourcil') ||
    id.includes('arcade') ||
    id.includes('eyebrow') ||
    id.includes('bridge')
  ) {
    return GUIDES_BY_ZONE_ID['body-zone-sourcil'];
  }

  if (name.includes('langue') || name.includes('tongue') || name === 'bouche' || id.includes('langue') || id.includes('bouche')) {
    return GUIDES_BY_ZONE_ID['body-zone-langue'];
  }

  if (
    name.includes('tour de bouche') ||
    name.includes('joue') ||
    name.includes('levre') ||
    name.includes('lip') ||
    id.includes('tour-de-bouche') ||
    id.includes('joue') ||
    id.includes('lip')
  ) {
    return GUIDES_BY_ZONE_ID['body-zone-tour-de-bouche'];
  }

  return null;
}
